import { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  CheckCircle, 
  Home, 
  Download, 
  Share2,
  QrCode,
  Mail,
  Phone,
  Package
} from 'lucide-react'
import { db } from '../lib/firebase'
import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import toast from 'react-hot-toast'
import '../styles/AssignSuccessPage.css'

export default function AssignSuccessPage() {
  const { qrId } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    // Vérifier que le QR code existe bien
    const verifyQRCode = async () => {
      try {
        const qrCodesRef = collection(db, 'qrCodes')
        const querySnapshot = await getDocs(qrCodesRef)
        
        const foundQr = querySnapshot.docs.find(doc => 
          doc.data().qrId === qrId || doc.id === qrId
        )
        
        if (!foundQr) {
          toast.error('QR code non trouvé')
          navigate('/')
          return
        }
        
        const qrData = foundQr.data()
        if (qrData.status !== 'assigned') {
          toast.error('Ce QR code n\'est pas encore assigné')
          navigate(`/assign/${qrId}`)
        }
      } catch (error) {
        console.error('Erreur vérification QR code:', error)
      }
    }
    
    verifyQRCode()
  }, [qrId, navigate])

  const handleDownloadInstructions = () => {
    const instructions = `
      INSTRUCTIONS POUR VOTRE QR CODE ULTIMATAG
      ========================================
      
      QR Code ID: ${qrId}
      
      VOTRE OBJET EST MAINTENANT PROTÉGÉ !
      
      1. COMMENT UTILISER VOTRE QR CODE :
         - Collez l'autocollant QR code sur votre objet
         - Assurez-vous qu'il soit visible et facile à scanner
         - Évitez de le placer sur des surfaces qui pourraient l'endommager
      
      2. SI VOTRE OBJET EST PERDU :
         - Toute personne qui trouve votre objet peut scanner le QR code
         - Vous recevrez une notification par email et SMS
         - Vous pourrez communiquer directement avec le trouveur
      
      3. CONSEILS IMPORTANTS :
         - Gardez cette page en favoris
         - Mettez à jour vos coordonnées si elles changent
         - Prenez une photo de votre objet pour référence
      
      4. SUPPORT :
         - Site web: ${window.location.origin}
         - Email: support@ultimatag.com
      
      MERCI DE VOTRE CONFIANCE !
    `
    
    const blob = new Blob([instructions], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ultimatag-instructions-${qrId}.txt`
    link.click()
    
    toast.success('Instructions téléchargées')
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mon objet est protégé avec UltimaTag',
          text: `J'ai protégé mon objet avec un QR code UltimaTag. Scannez-le si vous le trouvez !`,
          url: `${window.location.origin}/scan/${qrId}`,
        })
      } catch (error) {
        console.log('Partage annulé')
      }
    } else {
      // Fallback pour les navigateurs qui ne supportent pas l'API Web Share
      navigator.clipboard.writeText(`${window.location.origin}/scan/${qrId}`)
      toast.success('Lien copié dans le presse-papier')
    }
  }

  return (
    <div className="success-page">
      <div className="success-container">
        <div className="success-icon">
          <CheckCircle size={80} />
        </div>
        
        <h1 className="success-title">Félicitations !</h1>
        <p className="success-subtitle">
          Votre objet est maintenant protégé avec UltimaTag
        </p>
        
        <div className="qr-code-summary">
          <div className="qr-code-badge">
            <QrCode size={20} />
            <span>QR Code: <strong>{qrId}</strong></span>
          </div>
        </div>
        
        <div className="success-message">
          <p>
            Votre QR code a été assigné avec succès à votre objet. 
            Si quelqu'un trouve votre objet et scanne le code, vous serez 
            immédiatement notifié.
          </p>
        </div>
        
        {/* <div className="action-cards">
          <div className="action-card">
            <div className="action-icon">
              <Download size={24} />
            </div>
            <h3 className="action-title">Télécharger les instructions</h3>
            <p className="action-description">
              Obtenez un guide d'utilisation pour votre QR code
            </p>
            <button 
              onClick={handleDownloadInstructions}
              className="action-btn"
            >
              Télécharger
            </button>
          </div>
          
          <div className="action-card">
            <div className="action-icon">
              <Share2 size={24} />
            </div>
            <h3 className="action-title">Partager</h3>
            <p className="action-description">
              Partagez avec vos proches pour qu'ils connaissent votre QR code
            </p>
            <button 
              onClick={handleShare}
              className="action-btn"
            >
              Partager
            </button>
          </div>
          
          <div className="action-card">
            <div className="action-icon">
              <Home size={24} />
            </div>
            <h3 className="action-title">Retour à l'accueil</h3>
            <p className="action-description">
              Retourner sur la page principale d'UltimaTag
            </p>
            <Link to="/" className="action-btn">
              Accueil
            </Link>
          </div>
        </div> */}
        
        <div className="next-steps">
          <h2 className="next-steps-title">Prochaines étapes</h2>
          <div className="steps-list">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Collez votre QR code</h3>
                <p>Placez l'autocollant sur votre objet de manière visible</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>En cas de perte</h3>
                <p>La personne vous contactera pour une restitution de votre objet</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Gardez vos coordonnées à jour</h3>
                <p>Assurez-vous que nous puissions vous contacter</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="contact-info">
          <h3 className="contact-title">Besoin d'aide ?</h3>
          <div className="contact-options">
            <div className="contact-option">
              <Mail size={16} />
              <span>support@ultimatag.com</span>
            </div>
            <div className="contact-option">
              <Phone size={16} />
              <span>+225 07 12 20 49 86</span>
            </div>
          </div>
        </div>
        
        <div className="success-footer">
          <p className="footer-text">
            Merci de faire confiance à UltimaTag pour protéger vos objets
          </p>
          <div className="footer-links">
            <Link to="/privacy">Politique de confidentialité</Link>
            <Link to="/terms">Conditions d'utilisation</Link>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  )
}